/**
 * ¿La brecha horizontal se resuelve MOVIENDO lo que ya hay, o hay que comprar?
 *
 * La diferencia importa: decirle a un cliente "instalá otro DEA" cuando bastaba
 * con correr el que tiene es un costo inventado, y decirle "movelo" cuando no
 * alcanza es una recomendación que no funciona. Por eso acá no se estima: para
 * 1 DEA hay una respuesta EXACTA, y para 2+ una simulación explícita.
 */

import { distanceMeters, isPointInPolygon } from './geo.js';
import { coveragePercent } from './coverage.js';

/** Proyección plana local en metros. Válida a escala de un recinto. */
function projectMeters(lat, lng, lat0, lng0) {
  const R = 6378137;
  const x = (((lng - lng0) * Math.PI) / 180) * R * Math.cos((lat0 * Math.PI) / 180);
  const y = (((lat - lat0) * Math.PI) / 180) * R;
  return [x, y];
}

/** Circunferencia que pasa por tres puntos. `null` si son colineales. */
function circumcircle(a, b, c) {
  const [ax, ay] = a;
  const [bx, by] = b;
  const [cx, cy] = c;
  const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
  if (Math.abs(d) < 1e-9) return null;

  const ux =
    ((ax * ax + ay * ay) * (by - cy) +
      (bx * bx + by * by) * (cy - ay) +
      (cx * cx + cy * cy) * (ay - by)) /
    d;
  const uy =
    ((ax * ax + ay * ay) * (cx - bx) +
      (bx * bx + by * by) * (ax - cx) +
      (cx * cx + cy * cy) * (bx - ax)) /
    d;

  return { cx: ux, cy: uy, r: Math.hypot(ax - ux, ay - uy) };
}

/**
 * Radio en metros del CÍRCULO MÍNIMO que envuelve el recinto.
 *
 * Si ese radio ≤ 150 m, UN solo DEA en su centro cubre el 100% con **certeza
 * matemática**: todo polígono cabe dentro del disco que envuelve sus vértices.
 * No es una simulación, es una garantía — y por eso para el caso de 1 DEA no
 * hace falta adivinar.
 *
 * Fuerza bruta sobre pares y tríos de vértices. Es O(n³), pero un perímetro real
 * tiene decenas de vértices, no miles.
 *
 * @returns {number|null} Radio en metros, o `null` sin perímetro suficiente.
 */
export function minEnclosingRadius(perimeter) {
  if (!perimeter || perimeter.length < 2) return null;

  const [lat0, lng0] = perimeter[0];
  const points = perimeter.map(([lat, lng]) => projectMeters(lat, lng, lat0, lng0));
  const contains = (cx, cy, r, p) => Math.hypot(p[0] - cx, p[1] - cy) <= r + 1e-6;

  let best = null;

  // Con dos puntos en el diámetro.
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const cx = (points[i][0] + points[j][0]) / 2;
      const cy = (points[i][1] + points[j][1]) / 2;
      const r = Math.hypot(points[i][0] - points[j][0], points[i][1] - points[j][1]) / 2;
      if (points.every((p) => contains(cx, cy, r, p)) && (!best || r < best.r)) {
        best = { cx, cy, r };
      }
    }
  }

  // Con tres puntos en la circunferencia.
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      for (let k = j + 1; k < points.length; k++) {
        const circle = circumcircle(points[i], points[j], points[k]);
        if (!circle) continue;
        if (
          points.every((p) => contains(circle.cx, circle.cy, circle.r, p)) &&
          (!best || circle.r < best.r)
        ) {
          best = circle;
        }
      }
    }
  }

  return best ? best.r : null;
}

/** Muestrea puntos interiores del recinto sobre una grilla de `size`×`size`. */
function samplePolygon(perimeter, size) {
  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;
  perimeter.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });

  const points = [];
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      const lat = minLat + ((maxLat - minLat) * (i + 0.5)) / size;
      const lng = minLng + ((maxLng - minLng) * (j + 0.5)) / size;
      if (isPointInPolygon(lat, lng, perimeter)) points.push([lat, lng]);
    }
  }
  return points;
}

const TARGET_GRID = 44;
const CANDIDATE_GRID = 22;

/**
 * Máxima cobertura alcanzable REUBICANDO óptimamente los `count` DEA existentes.
 *
 * Greedy: en cada paso elige el candidato que cubre más puntos aún descubiertos.
 * No garantiza el óptimo global —es el problema de máxima cobertura, NP-difícil—
 * pero da una cota inferior honesta: si el greedy llega al 100%, reubicando se
 * llega seguro.
 *
 * @returns {number|null} Porcentaje 0-100, o `null` si no es calculable.
 */
export function optimalCoveragePercent(perimeter, count, radiusMeters) {
  if (!perimeter || perimeter.length < 3 || !count) return null;

  const targets = samplePolygon(perimeter, TARGET_GRID);
  if (!targets.length) return null;

  const candidates = samplePolygon(perimeter, CANDIDATE_GRID);
  const covered = new Array(targets.length).fill(false);

  for (let placed = 0; placed < count; placed++) {
    let best = null;
    let bestGain = -1;

    for (const candidate of candidates) {
      let gain = 0;
      for (let i = 0; i < targets.length; i++) {
        if (covered[i]) continue;
        if (distanceMeters(candidate[0], candidate[1], targets[i][0], targets[i][1]) <= radiusMeters) {
          gain++;
        }
      }
      if (gain > bestGain) {
        bestGain = gain;
        best = candidate;
      }
    }

    if (!best || bestGain <= 0) break;
    for (let i = 0; i < targets.length; i++) {
      if (covered[i]) continue;
      if (distanceMeters(best[0], best[1], targets[i][0], targets[i][1]) <= radiusMeters) {
        covered[i] = true;
      }
    }
  }

  return Math.round((covered.filter(Boolean).length / targets.length) * 100);
}

/**
 * ¿Hay un DEA de terreno REDUNDANTE, que se pueda mover sin perder cobertura?
 *
 * Redundante = al quitarlo, el porcentaje cubierto no baja, porque su zona ya la
 * cubren los demás. Ese equipo se puede llevar a la brecha.
 */
export function hasRedundantDea(perimeter, centers, radiusMeters) {
  if (!centers || centers.length < 2) return false;
  const base = coveragePercent(perimeter, centers, radiusMeters);
  if (base == null) return false;

  for (let k = 0; k < centers.length; k++) {
    const without = centers.filter((_, i) => i !== k);
    if (coveragePercent(perimeter, without, radiusMeters) === base) return true;
  }
  return false;
}

/**
 * ¿Reubicando lo que ya hay se llega al 100% de cobertura?
 *
 * Con 1 solo DEA la respuesta es exacta vía círculo mínimo. Con 2+ se simula.
 * Se separa a propósito: la certeza matemática vale más que una simulación, y
 * el caso de 1 DEA es el más frecuente en recintos chicos.
 *
 * MEMOIZADO: la interfaz recalcula el estado en cada tecla, y esto es O(n³) para
 * el círculo mínimo o una simulación sobre ~2.000 puntos para el greedy. Sin
 * caché, escribir el nombre del cliente congela el panel.
 */
let cache = null;

export function relocationReaches100(perimeter, deaCount, radiusMeters) {
  if (!perimeter || perimeter.length < 3 || !deaCount) return false;

  const key = `${JSON.stringify(perimeter)}|${deaCount}|${radiusMeters}`;
  if (cache && cache.key === key) return cache.value;

  const value =
    deaCount === 1
      ? (() => {
          const radius = minEnclosingRadius(perimeter);
          return radius != null && radius <= radiusMeters;
        })()
      : optimalCoveragePercent(perimeter, deaCount, radiusMeters) === 100;

  cache = { key, value };
  return value;
}

/** Sólo para tests: olvida el resultado memoizado. */
export function clearRelocationCache() {
  cache = null;
}
